import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Send, 
  MessageCircle, 
  User, 
  Shield, 
  Car,
  Loader2,
  Phone,
  ExternalLink
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function OperatorChat({ 
  alertId, 
  userId, 
  driverId, 
  userName,
  driverName,
  onWhatsAppClick 
}) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(null);
  const [loading, setLoading] = useState(true);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Load initial messages
  useEffect(() => {
    const loadMessages = async () => {
      const token = localStorage.getItem('mercapoli_operator_token');
      try {
        const res = await fetch(`${API_BASE}/api/chat/${alertId}/messages`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setMessages(data);
        }
      } catch (error) {
        console.error('Error loading messages:', error);
      } finally {
        setLoading(false);
      }
    };

    if (alertId) {
      loadMessages();
    }
  }, [alertId]);

  // WebSocket connection
  useEffect(() => {
    if (!alertId) return;

    const token = localStorage.getItem('mercapoli_operator_token');
    const wsUrl = `${API_BASE.replace('https://', 'wss://').replace('http://', 'ws://')}/ws/chat/${alertId}?token=${token}`;
    
    const connect = () => {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('Chat WebSocket connected');
        setIsConnected(true);
      };
      
      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        switch (data.type) {
          case 'new_message':
            setMessages(prev => [...prev, data]);
            break;
          case 'typing':
            setIsTyping(data.user_name);
            setTimeout(() => setIsTyping(null), 2000);
            break;
          case 'user_joined':
          case 'user_left':
            // Could show notification
            break;
          default:
            break;
        }
      };
      
      wsRef.current.onclose = () => {
        console.log('Chat WebSocket disconnected');
        setIsConnected(false);
        // Reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(connect, 3000);
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    };

    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [alertId]);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    // Try WebSocket first
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'message',
        content: newMessage
      }));
    } else {
      // Fallback to REST API
      const token = localStorage.getItem('mercapoli_operator_token');
      try {
        await fetch(`${API_BASE}/api/chat/${alertId}/message`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ content: newMessage })
        });
      } catch (error) {
        console.error('Error sending message:', error);
        return;
      }
    }

    setNewMessage('');
  };

  const handleTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing' }));
    }
  };

  const getSenderIcon = (senderType) => {
    switch (senderType) {
      case 'operator':
        return <Shield className="w-4 h-4 text-red-500" />;
      case 'driver':
        return <Car className="w-4 h-4 text-green-500" />;
      default:
        return <User className="w-4 h-4 text-blue-500" />;
    }
  };

  const getSenderBg = (senderType) => {
    switch (senderType) {
      case 'operator':
        return 'bg-red-50 border-red-100';
      case 'driver':
        return 'bg-green-50 border-green-100';
      default:
        return 'bg-blue-50 border-blue-100';
    }
  };

  if (loading) {
    return (
      <Card className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col bg-white border-slate-200">
      <CardHeader className="py-3 px-4 border-b border-slate-100 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-red-500" />
            <CardTitle className="text-sm font-medium text-slate-800">
              Chat de Emergencia
            </CardTitle>
            <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
          </div>
          
          {/* Quick Contact Buttons */}
          <div className="flex items-center gap-1">
            {userId && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-blue-600 hover:bg-blue-50"
                onClick={() => onWhatsAppClick?.(userId, 'user')}
              >
                <Phone className="w-3 h-3 mr-1" />
                Usuario
              </Button>
            )}
            {driverId && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-green-600 hover:bg-green-50"
                onClick={() => onWhatsAppClick?.(driverId, 'driver')}
              >
                <Phone className="w-3 h-3 mr-1" />
                Conductor
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No hay mensajes</p>
            <p className="text-xs mt-1">Inicia la conversación con el usuario o conductor</p>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div 
              key={msg.id || idx} 
              className={`flex gap-2 ${msg.sender_type === 'operator' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.sender_type !== 'operator' && (
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                  {getSenderIcon(msg.sender_type)}
                </div>
              )}
              
              <div className={`max-w-[75%] ${msg.sender_type === 'operator' ? 'order-first' : ''}`}>
                <div className={`px-3 py-2 rounded-xl border ${getSenderBg(msg.sender_type)}`}>
                  {msg.sender_type !== 'operator' && (
                    <p className="text-xs font-medium text-slate-600 mb-1">
                      {msg.sender_name}
                    </p>
                  )}
                  <p className="text-sm text-slate-800">{msg.content}</p>
                </div>
                <p className="text-xs text-slate-400 mt-1 px-2">
                  {new Date(msg.created_at).toLocaleTimeString('es-GT', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </p>
              </div>
              
              {msg.sender_type === 'operator' && (
                <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  {getSenderIcon(msg.sender_type)}
                </div>
              )}
            </div>
          ))
        )}
        
        {isTyping && (
          <div className="flex items-center gap-2 text-slate-400 text-xs">
            <span className="flex gap-1">
              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </span>
            {isTyping} está escribiendo...
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </CardContent>
      
      {/* Input */}
      <div className="p-3 border-t border-slate-100 flex-shrink-0">
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            onInput={handleTyping}
            placeholder="Escribe un mensaje..."
            className="flex-1 bg-slate-50 border-slate-200"
            data-testid="chat-input"
          />
          <Button
            onClick={sendMessage}
            disabled={!newMessage.trim()}
            className="bg-red-500 hover:bg-red-600 text-white"
            data-testid="chat-send-button"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
